import { useCallback, useRef } from "react";

/**
 * Custom hook for managing automatic data refresh with debouncing
 * Prevents excessive API calls and maintains filter state during refresh
 * @param {function} fetchFunction - async function to call for data refresh
 * @param {number} debounceMs - milliseconds to debounce refetch (default: 500)
 * @returns {object} { refetch, triggerRefetch }
 */
export const useAutoRefresh = (fetchFunction, debounceMs = 500) => {
  const debounceTimerRef = useRef(null);

  // Refetch with debouncing
  const refetch = useCallback(
    async (params = {}) => {
      // Clear existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new debounce timer
      debounceTimerRef.current = setTimeout(async () => {
        try {
          await fetchFunction(params);
        } catch (error) {
          console.error("Auto-refresh error:", error);
        }
      }, debounceMs);
    },
    [fetchFunction, debounceMs]
  );

  // Trigger immediate refetch (skip debounce)
  const triggerRefetch = useCallback(
    async (params = {}) => {
      // Clear debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      try {
        await fetchFunction(params);
      } catch (error) {
        console.error("Refetch error:", error);
      }
    },
    [fetchFunction]
  );

  return {
    refetch,
    triggerRefetch,
  };
};

export default useAutoRefresh;
